import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { authService, User } from "../services/api";
import { SUPABASE_URL } from "../config/supabase";
import toast from "react-hot-toast";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refreshUser: () => Promise<void>;
  loginWithGoogle: () => void;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  bio?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Verify token and get user data
      authService
        .getCurrentUser()
        .then((userData) => {
          setUser(userData);
        })
        .catch(() => {
          localStorage.removeItem("token");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await authService.login({ email, password });
      localStorage.setItem("token", response.access_token);

      const userData = await authService.getCurrentUser();
      setUser(userData);

      console.log("AuthContext - Current user:", userData);
      toast.success(`Welcome back, ${userData.username}!`);
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error logging in");
      return false;
    }
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    try {
      await authService.register(userData);
      toast.success("Account created successfully! Verify your email.");
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error creating account");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Session closed successfully");
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    } else {
      setUser(userData as User);
    }
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const userData = await authService.getCurrentUser();
        setUser((prev) => {
          // Simple check to avoid unnecessary re-renders if nothing changed
          if (JSON.stringify(prev) !== JSON.stringify(userData)) {
            return userData;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Background refresh failed", error);
    }
  };

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refreshUser, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [user]);

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    loginWithGoogle: () => {
      const redirectTo = `${window.location.origin}/auth/callback`;
      const authorizeUrl = `${SUPABASE_URL}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(
        redirectTo
      )}`;
      window.location.href = authorizeUrl;
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
